import React from "react";
import { Link } from "react-router-dom";

/**
 * BCPeabody brand identity system.
 *
 * variant:
 *   "full"    → diamond + BCPeabody wordmark + copper rule + descriptor.
 *              Use in desktop header and footer.
 *   "compact" → diamond + BCPeabody wordmark, no rule / descriptor.
 *              Use in tablet nav and inline contexts.
 *   "icon"    → diamond mark alone. Use in small mobile header, favicon,
 *              or wherever the wordmark would compete with adjacent copy.
 *   "seal"    → nested double-diamond outline seal. Use sparingly (max 2
 *              instances per page) on dark feature sections and dividers.
 *
 * All variants render as a link to "/" unless linked={false}.
 */

function DiamondMark({ size = 40, filled = true }) {
  // Diamond with BCP inside. `filled` matches the front-of-card style,
  // outline-only diamond used for the seal variant.
  const stroke = "#b28451"; // copper
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {filled ? (
        <>
          <polygon points="50,4 96,50 50,96 4,50" fill={stroke} />
          <text
            x="50"
            y="60"
            textAnchor="middle"
            fontFamily="'Playfair Display', Georgia, serif"
            fontWeight="700"
            fontSize="30"
            fill="#1f1c17"
            letterSpacing="1"
          >
            BCP
          </text>
        </>
      ) : (
        <>
          <polygon points="50,4 96,50 50,96 4,50" fill="none" stroke={stroke} strokeWidth="1.2" />
          <polygon points="50,20 82,50 50,80 18,50" fill="none" stroke={stroke} strokeWidth="1" opacity="0.6" />
        </>
      )}
    </svg>
  );
}

function Wordmark({ tone = "dark" }) {
  // "BCP" in copper, "eabody" in the current tone.
  const bodyColor = tone === "light" ? "#f4efe6" : "#1f1c17";
  return (
    <span
      style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontWeight: 700,
        fontSize: "1.5rem",
        letterSpacing: "-0.005em",
        lineHeight: 1,
      }}
    >
      <span style={{ color: "#b28451" }}>BCP</span>
      <span style={{ color: bodyColor }}>eabody</span>
    </span>
  );
}

export default function BrandMark({
  variant = "compact",
  linked = true,
  tone = "dark",
  size = "md",
  className = "",
}) {
  const diamondSize = size === "sm" ? 30 : size === "lg" ? 56 : 40;
  const textToneCls = tone === "light" ? "text-cream-light/70" : "text-ink/70";
  const ruleColor = "#b28451";

  let content;
  if (variant === "seal") {
    content = <DiamondMark size={diamondSize} filled={false} />;
  } else if (variant === "icon") {
    content = <DiamondMark size={diamondSize} filled={true} />;
  } else if (variant === "compact") {
    content = (
      <span className="inline-flex items-center gap-3">
        <DiamondMark size={diamondSize} filled={true} />
        <Wordmark tone={tone} />
      </span>
    );
  } else {
    // full
    content = (
      <span className="inline-flex items-start gap-3">
        <DiamondMark size={diamondSize} filled={true} />
        <span className="flex flex-col justify-center">
          <Wordmark tone={tone} />
          <span
            aria-hidden="true"
            style={{
              display: "block",
              width: "100%",
              height: 1,
              background: ruleColor,
              margin: "6px 0 4px",
              opacity: 0.7,
            }}
          />
          <span
            className={`text-[10px] uppercase tracking-[0.18em] ${textToneCls}`}
            style={{ whiteSpace: "nowrap" }}
          >
            General Contractor · Project Management · Consulting
          </span>
        </span>
      </span>
    );
  }

  if (!linked) return <span className={className}>{content}</span>;
  return (
    <Link to="/" data-testid="brand-mark" className={className}>
      {content}
    </Link>
  );
}
